import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Users, Building2, Grid3X3, BookmarkCheck,
  Handshake, Calendar, UserPlus, Newspaper, Image, MessageSquare,
  Settings, LogOut, ChevronLeft, Menu, ExternalLink, FileText, Lock, Home
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', end: true, testId: 'admin' },
    ]
  },
  {
    label: 'Contenu',
    items: [
      { to: '/admin/site-builder', icon: FileText, label: 'Site Builder' },
      { to: '/admin/intervenants', icon: Users, label: 'Intervenants' },
      { to: '/admin/exposants', icon: Building2, label: 'Exposants' },
      { to: '/admin/stands', icon: Grid3X3, label: 'Stands' },
      { to: '/admin/reservations', icon: BookmarkCheck, label: 'Réservations' },
      { to: '/admin/partenaires', icon: Handshake, label: 'Partenaires' },
      { to: '/admin/programme', icon: Calendar, label: 'Programme' },
      { to: '/admin/actualites', icon: Newspaper, label: 'Actualités' },
    ]
  },
  {
    label: 'Communication',
    items: [
      { to: '/admin/inscriptions', icon: UserPlus, label: 'Inscriptions' },
      { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
      { to: '/admin/galerie', icon: Image, label: 'Bibliothèque médias' },
    ]
  },
  {
    label: 'Système',
    items: [
      { to: '/admin/parametres', icon: Settings, label: 'Paramètres' },
    ]
  }
];

const linkBase = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200";
const linkActive = "bg-white/15 text-white";
const linkInactive = "text-white/60 hover:text-white hover:bg-white/10";

export default function AdminLayout() {
  const { user, isAdmin, loading, logout, token } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Robust access guard: instead of a silent redirect (which can show a blank flash),
  // we render a clear "Access required" screen with explicit actions.
  if (!isAdmin) {
    const tokenExpired = !!token; // had a token but auth failed (likely expired)
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-4" data-testid="admin-access-denied">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[#E1E8F0] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFF7E5] flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-[#F5A623]" />
          </div>
          <h1 className="text-xl font-bold text-[#1E3A5F] mb-2">
            {tokenExpired ? 'Session expirée' : 'Accès administrateur requis'}
          </h1>
          <p className="text-sm text-[#6B7C93] leading-relaxed mb-6">
            {tokenExpired
              ? 'Votre session a expiré ou est devenue invalide. Veuillez vous reconnecter pour continuer.'
              : "Cette zone est réservée aux administrateurs. Connectez-vous avec un compte admin pour y accéder."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => { logout(); navigate('/'); }}
              className="bg-[#1E3A5F] hover:bg-[#152A45] text-white"
              data-testid="login-redirect-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Se reconnecter
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Button>
          </div>
          <p className="text-xs text-[#94A3B8] mt-6">
            Astuce : sur l'accueil, triple-cliquez sur le logo URBANIK en pied de page pour ouvrir la connexion admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#1E3A5F] z-40 transition-all duration-300 flex flex-col ${collapsed ? 'w-[68px]' : 'w-[250px]'}`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {!collapsed && (
            <div className="flex items-center gap-2.5">
              <img src="/logo-urbanik.png" alt="URBANIK" className="h-8 w-auto brightness-0 invert" />
            </div>
          )}
          {collapsed && (
            <img src="/logo-urbanik.png" alt="U" className="h-7 w-7 object-contain object-left brightness-0 invert" />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-white/60 hover:text-white p-1 rounded"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label || 'main'}>
              {section.label && !collapsed && (
                <p className="text-[10px] uppercase tracking-widest text-white/30 font-semibold mb-2 px-3">
                  {section.label}
                </p>
              )}
              {collapsed && section.label && <div className="border-t border-white/10 my-2" />}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                    title={collapsed ? item.label : undefined}
                    data-testid={`nav-${item.testId || item.to.split('/').pop() || 'dashboard'}`}
                  >
                    <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/10 p-3 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`${linkBase} ${linkInactive}`}
            data-testid="nav-view-site"
          >
            <ExternalLink className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && <span>Voir le site</span>}
          </a>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className={`${linkBase} ${linkInactive} w-full`}
            data-testid="nav-logout"
          >
            <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'ml-[68px]' : 'ml-[250px]'}`}>
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-[#E1E8F0] flex items-center justify-between px-8 sticky top-0 z-30">
          <div />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-[#1E3A5F]">{user?.full_name}</p>
              <p className="text-xs text-[#6B7C93]">Administrateur</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#1E3A5F] flex items-center justify-center">
              <span className="text-white text-sm font-semibold">{user?.full_name?.[0] || 'A'}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
